
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { NotificationsDropdown } from './NotificationsDropdown';
import { AuthModal } from './AuthModal';
import AvatarDisplay from './AvatarDisplay';

export default function Header() {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const { user, profile, signOut, initializing } = useAuth();
  const navigate = useNavigate();

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-user-menu]')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleUserSignOut = async () => {
    try {
      await signOut();
      setShowUserMenu(false);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSignInClick = () => {
    setAuthMode('login');
    setShowAuthModal(true);
  };

  const handleSignUpClick = () => {
    setAuthMode('signup');
    setShowAuthModal(true);
  };

  const handleMessagesClick = () => {
    if (user) {
      navigate('/messages');
    } else {
      setAuthMode('login');
      setShowAuthModal(true);
    }
  };

  return (
    <>
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-3">
                <img
                  src="https://static.readdy.ai/image/fdabe7c7ccee11942d3988daf4f91aee/f49c6930ed0cac90b3c370f0e31f809b.png"
                  alt="Filmora Logo"
                  className="h-8 w-auto"
                />
                <span
                  className="text-xl font-bold text-gray-900"
                  style={{ fontFamily: 'Pacifico, serif' }}
                >
                  Filmora
                </span>
              </Link>
            </div>

            <nav className="hidden md:flex items-center space-x-8">
              <Link
                to="/"
                className="flex items-center space-x-2 text-gray-700 hover:text-purple-600 font-medium"
              >
                <span className="w-5 h-5 flex items-center justify-center text-gray-800">
                  <i className="ri-home-fill text-lg"></i>
                </span>
                <span>Home</span>
              </Link>
              <Link
                to="/forum"
                className="flex items-center space-x-2 text-gray-700 hover:text-purple-600 font-medium"
              >
                <span className="w-5 h-5 flex items-center justify-center text-green-500">
                  <i className="ri-discuss-fill text-lg"></i>
                </span>
                <span>Forum</span>
              </Link>
              <Link
                to="/creators"
                className="flex items-center space-x-2 text-gray-700 hover:text-purple-600 font-medium"
              >
                <span className="w-5 h-5 flex items-center justify-center text-pink-400">
                  <i className="ri-user-star-fill text-lg"></i>
                </span>
                <span>Creators</span>
              </Link>
              <Link
                to="/dramas"
                className="flex items-center space-x-2 text-gray-700 hover:text-purple-600 font-medium"
              >
                <span className="w-5 h-5 flex items-center justify-center text-amber-400">
                  <i className="ri-movie-2-fill text-lg"></i>
                </span>
                <span>Dramas</span>
              </Link>
              <Link
                to="/groups"
                className="flex items-center space-x-2 text-gray-700 hover:text-purple-600 font-medium"
              >
                <span className="w-5 h-5 flex items-center justify-center text-teal-500">
                  <i className="ri-group-fill text-lg"></i>
                </span>
                <span>Groups</span>
              </Link>
            </nav>

            <div className="flex items-center space-x-4">
              {/* Notifications Dropdown */}
              <NotificationsDropdown />

              <button
                onClick={handleMessagesClick}
                className="p-2 text-gray-600 hover:text-purple-600 transition-colors cursor-pointer"
              >
                <i className="ri-message-3-line text-xl"></i>
              </button>

              {/* User Menu */}
              {initializing ? (
                <div className="w-10 h-10 rounded-full bg-gray-100 animate-pulse"></div>
              ) : user ? (
                <div className="relative" data-user-menu>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <AvatarDisplay
                      avatarId={profile?.avatar_id}
                      username={profile?.username}
                      email={user.email}
                      size="md"
                    />
                    <span className="text-gray-700 font-medium hidden sm:block">
                      {profile?.username || 'User'}
                    </span>
                    <i className="ri-arrow-down-s-line text-gray-500"></i>
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <i className="ri-user-line mr-3"></i>
                        Profile
                      </Link>
                      <Link
                        to="/settings"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <i className="ri-settings-3-line mr-3"></i>
                        Settings
                      </Link>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          handleMessagesClick();
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <i className="ri-message-3-line mr-3"></i>
                        Messages
                      </button>
                      <div className="border-t border-gray-100 my-1"></div>
                      <button
                        onClick={handleUserSignOut}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <i className="ri-logout-box-line mr-3"></i>
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleSignInClick}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={handleSignUpClick}
                    className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Sign Up
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authMode}
      />
    </>
  );
}
